import { useEffect } from "react";
import { SITE, canonical } from "@/lib/site";
import { setShareMeta } from "@/lib/shareMeta";
import { fitDescription } from "@/lib/metaDescription";
import { resolveOgImage } from "@/lib/ogImage";

/**
 * Direct-DOM head manager.
 *
 * We previously used react-helmet-async, but Helmet on the client appends
 * its own tags without reclaiming pre-existing prerendered ones (the
 * `data-rh="true"` marker only deduplicates against tags Helmet itself
 * created in the same process , typically during server-side rendering).
 * That produced duplicate <title>/<meta description>/<link canonical>
 * pairs which Bing reported as SEO errors.
 *
 * This component updates the prerendered tags in place via the DOM API,
 * so each tag exists exactly once at all times. Tags it has to create
 * itself are marked with `data-seo="managed"` so they can be replaced
 * (rather than appended) on subsequent renders, including SPA route
 * changes.
 */

function absoluteUrl(maybeRelative: string): string {
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return `${SITE.url}${maybeRelative.startsWith("/") ? maybeRelative : `/${maybeRelative}`}`;
}

interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  publishedAt?: string;
  modifiedAt?: string;
  keywords?: string[];
  noindex?: boolean;
  jsonLd?: object | object[];
  /**
   * Shrinktionary canonical concept slug for this page, when it maps to one.
   * Emits `<meta name="shrink:canonical-concept">` so cross-network consumers
   * can route by concept identity rather than URL guessing (Rule 3 of the
   * multidirectional-network spec).
   */
  canonicalConcept?: string;
  /** Alt text for og:image / twitter:image. Defaults to the page title. */
  imageAlt?: string;
}

type Attr =
  | { kind: "name"; value: string }
  | { kind: "property"; value: string }
  | { kind: "link-rel"; value: string };

function upsertMeta(attr: Attr, content: string | null) {
  const head = document.head;
  let selector: string;
  let tagName: string;
  if (attr.kind === "name") {
    selector = `meta[name="${attr.value}"]`;
    tagName = "meta";
  } else if (attr.kind === "property") {
    selector = `meta[property="${attr.value}"]`;
    tagName = "meta";
  } else {
    selector = `link[rel="${attr.value}"]`;
    tagName = "link";
  }
  let el = head.querySelector(selector) as HTMLElement | null;
  if (content === null || content === "") {
    if (el && el.getAttribute("data-seo") === "managed") {
      el.remove();
    }
    return;
  }
  if (!el) {
    el = document.createElement(tagName);
    if (attr.kind === "name") el.setAttribute("name", attr.value);
    else if (attr.kind === "property") el.setAttribute("property", attr.value);
    else el.setAttribute("rel", attr.value);
    el.setAttribute("data-seo", "managed");
    head.appendChild(el);
  }
  if (tagName === "link") {
    el.setAttribute("href", content);
  } else {
    el.setAttribute("content", content);
  }
}

function setManagedJsonLd(blocks: object[]) {
  const head = document.head;
  head
    .querySelectorAll('script[type="application/ld+json"][data-seo="managed"]')
    .forEach((n) => n.remove());
  for (const block of blocks) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "managed");
    script.textContent = JSON.stringify(block);
    head.appendChild(script);
  }
}

export default function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  publishedAt,
  modifiedAt,
  keywords,
  noindex = false,
  jsonLd,
  canonicalConcept,
  imageAlt,
}: SeoProps) {
  const url = canonical(path);
  // Pack K: titles are authoritative as-written. No auto-suffix.
  // Mirrors formatTitle() in scripts/prerender.mts so prerendered HTML
  // and hydrated runtime <title> stay byte-identical.
  const fullTitle = title;
  // Network standard: 120 to 160 characters, ending on a full sentence.
  // Same function the prerender uses, so raw and hydrated HTML match.
  description = fitDescription(description);
  const ogImageAlt = imageAlt ?? `${title}, AnxietyResource`;
  // Medically reviewed content names the physician; site chrome and legal
  // pages keep the brand.
  const authorValue =
    type === "article" ? "Shariq Refai, MD, MBA, FAPA" : "AnxietyResource";
  const ogImage = absoluteUrl(resolveOgImage(path, image));
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const ldKey = JSON.stringify(ldArray);
  const keywordsValue = keywords && keywords.length > 0 ? keywords.join(", ") : null;

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = fullTitle;
    upsertMeta({ kind: "name", value: "description" }, description);
    upsertMeta({ kind: "name", value: "keywords" }, keywordsValue);
    upsertMeta({ kind: "name", value: "author" }, authorValue);
    upsertMeta({ kind: "link-rel", value: "canonical" }, url);
    upsertMeta(
      { kind: "name", value: "robots" },
      noindex ? "noindex, nofollow" : null,
    );
    upsertMeta({ kind: "property", value: "og:site_name" }, SITE.name);
    upsertMeta({ kind: "property", value: "og:title" }, fullTitle);
    upsertMeta({ kind: "property", value: "og:description" }, description);
    upsertMeta({ kind: "property", value: "og:type" }, type);
    upsertMeta({ kind: "property", value: "og:url" }, url);
    upsertMeta({ kind: "property", value: "og:image" }, ogImage);
    upsertMeta({ kind: "property", value: "og:image:alt" }, ogImageAlt);
    upsertMeta({ kind: "name", value: "twitter:image:alt" }, ogImageAlt);
    upsertMeta(
      { kind: "property", value: "article:published_time" },
      type === "article" && publishedAt ? publishedAt : null,
    );
    upsertMeta(
      { kind: "property", value: "article:modified_time" },
      type === "article" && modifiedAt ? modifiedAt : null,
    );
    upsertMeta({ kind: "name", value: "twitter:card" }, "summary_large_image");
    upsertMeta({ kind: "name", value: "twitter:title" }, fullTitle);
    upsertMeta({ kind: "name", value: "twitter:description" }, description);
    upsertMeta({ kind: "name", value: "twitter:image" }, ogImage);
    upsertMeta(
      { kind: "name", value: "shrink:canonical-concept" },
      canonicalConcept ?? null,
    );
    setManagedJsonLd(ldArray);
    setShareMeta({ title: fullTitle, description, noindex, path });
  }, [
    fullTitle,
    description,
    url,
    ogImage,
    type,
    publishedAt,
    modifiedAt,
    keywordsValue,
    noindex,
    ldKey,
    ldArray,
    canonicalConcept,
    authorValue,
    ogImageAlt,
  ]);

  return null;
}
